import { useState } from "react";
import {
  Button,
  Box,
  Typography,
  Paper,
  TextField,
  Link,
  useTheme,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import api from '../services/api.js';
import { useNavigate } from 'react-router-dom';

export default function ContactForm() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [tipoPessoa, setTipoPessoa] = useState('fisica');
  const theme = useTheme();
  const navigate = useNavigate();

  // Campos para pessoa física
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');

  // Campos para pessoa jurídica
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [telefoneEmpresa, setTelefoneEmpresa] = useState('');
  const [emailEmpresa, setEmailEmpresa] = useState('');
  const [responsavel, setResponsavel] = useState('');

  // Campos comuns
  const [mensagem, setMensagem] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validações básicas
    if (tipoPessoa === 'fisica') {
      if (!nomeCompleto || !cpf || !telefone || !email) {
        setMessage({ type: 'error', text: 'Por favor, preencha todos os campos obrigatórios.' });
        return;
      }
    } else {
      if (!razaoSocial || !cnpj || !telefoneEmpresa || !emailEmpresa || !responsavel) {
        setMessage({ type: 'error', text: 'Por favor, preencha todos os campos obrigatórios.' });
        return;
      }
    }

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailToValidate = tipoPessoa === 'fisica' ? email : emailEmpresa;
    if (!emailRegex.test(emailToValidate)) {
      setMessage({ type: 'error', text: 'Por favor, informe um e-mail válido.' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const dados = {
        tipo_pessoa: tipoPessoa,
        ...(tipoPessoa === 'fisica' ? {
          nome_completo: nomeCompleto,
          cpf: cpf,
          telefone: telefone,
          email: email
        } : {
          razao_social: razaoSocial,
          nome_fantasia: nomeFantasia,
          cnpj: cnpj,
          telefone: telefoneEmpresa,
          email: emailEmpresa,
          responsavel: responsavel
        }),
        mensagem: mensagem || ''
      };

      const response = await api.post('/auth/contact', dados);

      if (response.data && response.data.status === 'sucesso') {
        setMessage({
          type: 'success',
          text: 'Ficamos muito contentes em receber seus dados! Em breve, um especialista entrará em contato com você.'
        });
        
        // Limpar formulário após sucesso
        setTimeout(() => {
          setNomeCompleto('');
          setCpf('');
          setTelefone('');
          setEmail('');
          setRazaoSocial('');
          setNomeFantasia('');
          setCnpj('');
          setTelefoneEmpresa('');
          setEmailEmpresa('');
          setResponsavel('');
          setMensagem('');
          setTipoPessoa('fisica');
        }, 3000);
      } else {
        setMessage({
          type: 'error',
          text: response.data?.mensagem || 'Erro ao enviar dados. Tente novamente mais tarde.'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar dados de contato:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.mensagem || 'Erro ao processar solicitação. Tente novamente mais tarde.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        padding: 2,
        position: 'relative'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 600,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Solicitar Contato
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Preencha os dados abaixo e nossa equipe entrará em contato em breve.
        </Typography>

        {message && message.text && (
          <Alert 
            severity={message.type === 'success' ? 'success' : 'error'} 
            sx={{ width: '100%', mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Tipo de Cadastro
            </Typography>
            <RadioGroup
              row
              value={tipoPessoa}
              onChange={(e) => setTipoPessoa(e.target.value)}
            >
              <FormControlLabel value="fisica" control={<Radio />} label="Pessoa Física" />
              <FormControlLabel value="juridica" control={<Radio />} label="Pessoa Jurídica" />
            </RadioGroup>
          </FormControl>

          {tipoPessoa === 'fisica' ? (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="nomeCompleto"
                label="Nome Completo"
                name="nomeCompleto"
                autoComplete="name"
                autoFocus
                value={nomeCompleto}
                onChange={(e) => setNomeCompleto(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="cpf"
                label="CPF"
                name="cpf"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                disabled={loading || message.type === 'success'}
                placeholder="000.000.000-00"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="telefone"
                label="Telefone"
                name="telefone"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                disabled={loading || message.type === 'success'}
                placeholder="(00) 00000-0000"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-mail"
                name="email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
            </>
          ) : (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="razaoSocial"
                label="Razão Social"
                name="razaoSocial"
                autoFocus
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
              <TextField
                margin="normal"
                fullWidth
                id="nomeFantasia"
                label="Nome Fantasia"
                name="nomeFantasia"
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="cnpj"
                label="CNPJ"
                name="cnpj"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                disabled={loading || message.type === 'success'}
                placeholder="00.000.000/0000-00"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="telefoneEmpresa"
                label="Telefone"
                name="telefoneEmpresa"
                value={telefoneEmpresa}
                onChange={(e) => setTelefoneEmpresa(e.target.value)}
                disabled={loading || message.type === 'success'}
                placeholder="(00) 00000-0000"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="emailEmpresa"
                label="E-mail"
                name="emailEmpresa"
                autoComplete="email"
                type="email"
                value={emailEmpresa}
                onChange={(e) => setEmailEmpresa(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="responsavel"
                label="Responsável"
                name="responsavel"
                value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)}
                disabled={loading || message.type === 'success'}
              />
            </>
          )}

          <TextField
            margin="normal"
            fullWidth
            id="mensagem"
            label="Mensagem (opcional)"
            name="mensagem"
            multiline
            rows={4}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            disabled={loading || message.type === 'success'}
            placeholder="Informe aqui qualquer informação adicional que considere relevante..."
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || message.type === 'success'}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {loading ? 'Enviando...' : 'Enviar Solicitação'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
              Voltar para o login
            </Link>
          </Box>
        </Box>
      </Paper>
      
      {/* Rodapé */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        textAlign: 'center',
        padding: '12px',
        fontSize: '0.875rem',
        zIndex: 2
      }}>
        <Typography variant="body2">
          Desenvolvido por SAOTech Software. Tel. (11) 9 2039-2120 - suporte@saoinformatica.com.br
        </Typography>
      </Box>
    </Box>
  );
}


